/**
 * Teacher open-shift pickup requests (BON-242) — /api/v1/me/shift-requests.
 * Self-scoped: the API always acts on the caller, so no user id is ever sent.
 *
 * Shapes mirror MyRequestEntry / EligibleShift in
 * backend/src/services/shift-pickup.service.ts — keep in sync.
 */
import { apiFetch } from '../lib/apiFetch';

/** BON-242 only ever produces these two. `confirmed`/`declined` land in BON-243. */
export type PickupRequestStatus = 'pending' | 'withdrawn';

export interface MyRequestEntry {
  openShiftId: string;
  startsAtIso: string;
  venueShort: string;
  workshopTypeName: string;
  position: string;
  status: PickupRequestStatus;
  requestedAtIso: string;
  /** Other people waiting on the same shift — the design's "+ N others waiting". */
  othersWaiting: number;
  /** False once the shift is withdrawn, its workshop cancelled, or the date has
   *  passed. Such a request can never be decided, so it must not sit under
   *  "Waiting on approval" implying someone is still considering it. */
  stillLive: boolean;
}

async function parseRequest(res: Response, fallback: string): Promise<MyRequestEntry> {
  let body: { request?: MyRequestEntry; message?: string } | null = null;
  try {
    body = (await res.json()) as { request?: MyRequestEntry; message?: string };
  } catch {
    body = null;
  }
  if (!res.ok || !body?.request) {
    throw new Error(body?.message ?? `${fallback} (HTTP ${res.status})`);
  }
  return body.request;
}

export async function fetchMyRequests(): Promise<MyRequestEntry[]> {
  const res = await apiFetch('/api/v1/me/shift-requests');
  if (!res.ok) throw new Error(`Failed to load your requests (HTTP ${res.status})`);
  return ((await res.json()) as { requests: MyRequestEntry[] }).requests;
}

/** Raise a hand. Not a booking — an RD confirms one person per shift. */
export async function requestShift(openShiftId: string): Promise<MyRequestEntry> {
  const res = await apiFetch(`/api/v1/me/shift-requests/${openShiftId}`, { method: 'POST' });
  return parseRequest(res, 'Failed to request the shift');
}

export async function withdrawShiftRequest(openShiftId: string): Promise<MyRequestEntry> {
  const res = await apiFetch(`/api/v1/me/shift-requests/${openShiftId}`, { method: 'DELETE' });
  return parseRequest(res, 'Failed to withdraw the request');
}
